import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

/**
 * Cache global de tags controladas resolvidas (id → nome).
 * Um único fetch batch por página, evita N+1.
 */
const TAG_NAME_CACHE = new Map();
const inflight = new Map();

async function resolveTagNames(ids) {
  const missing = ids.filter((id) => !TAG_NAME_CACHE.has(id));
  if (missing.length === 0) return;
  const key = missing.slice().sort().join('|');
  if (inflight.has(key)) {
    await inflight.get(key);
    return;
  }
  const p = (async () => {
    try {
      // Não temos endpoint batch-por-ids, então buscamos por termo=id (slug).
      // Para eficiência, uso apenas os 20 primeiros e assumimos que o backend
      // pode ampliar depois. Fallback: mostrar id.
      for (const id of missing) {
        try {
          const r = await api.get('/tags/buscar', { params: { termo: id, limit: 3 } });
          const hit = (r?.data?.items || []).find((t) => t.id === id);
          if (hit) TAG_NAME_CACHE.set(id, hit.nome);
          else TAG_NAME_CACHE.set(id, id);
        } catch (_e) {
          TAG_NAME_CACHE.set(id, id);
        }
      }
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  await p;
}

/**
 * Chip neutro de tag de especialização — sempre texto puro, sem logotipo.
 */
export function TagChip({ children, size = 'sm', testId }) {
  const cls =
    size === 'sm'
      ? 'text-[11px] px-2 py-0.5'
      : 'text-xs px-2.5 py-1';
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium bg-[var(--primary)]/8 text-[var(--primary)] border border-[var(--primary)]/15 ${cls}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

/**
 * Renderiza os chips de tags_controladas_ids do negócio.
 * - Mostra até `limit` chips e um "+N" ao final se houver mais.
 * - Se `expandable`, o "+N" abre para mostrar todas ao clicar.
 * - Se `preResolvedNames` for passado (mapa id→nome), pula a API e usa direto.
 */
export default function BusinessTagsChips({
  business,
  limit = 4,
  size = 'sm',
  expandable = false,
  preResolvedNames = null,
  testIdPrefix = 'biz-tag',
}) {
  const ids = business?.tags_controladas_ids || [];
  const [names, setNames] = useState(() => {
    if (preResolvedNames) return preResolvedNames;
    // Warm start via cache
    const seed = {};
    ids.forEach((id) => {
      if (TAG_NAME_CACHE.has(id)) seed[id] = TAG_NAME_CACHE.get(id);
    });
    return seed;
  });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (preResolvedNames || ids.length === 0) return;
    let alive = true;
    (async () => {
      await resolveTagNames(ids);
      if (!alive) return;
      const out = {};
      ids.forEach((id) => { out[id] = TAG_NAME_CACHE.get(id) || id; });
      setNames(out);
    })();
    return () => { alive = false; };
  }, [ids, preResolvedNames]);

  if (!ids || ids.length === 0) return null;

  const displayIds = expanded ? ids : ids.slice(0, limit);
  const extra = Math.max(0, ids.length - limit);

  return (
    <div className="flex flex-wrap gap-1" data-testid={`${testIdPrefix}s`}>
      {displayIds.map((id) => (
        <TagChip key={id} size={size} testId={`${testIdPrefix}-${id}`}>
          {names[id] || id}
        </TagChip>
      ))}
      {!expanded && extra > 0 && (
        expandable ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
            className="text-[11px] font-medium text-[var(--primary)] px-2 py-0.5 hover:underline"
            data-testid={`${testIdPrefix}s-more`}
          >
            e mais {extra}
          </button>
        ) : (
          <span
            className="text-[11px] font-medium text-[var(--text-muted)] px-2 py-0.5"
            data-testid={`${testIdPrefix}s-more-label`}
          >
            e mais {extra}
          </span>
        )
      )}
    </div>
  );
}
